import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator, Platform, Alert } from 'react-native';
import { useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { observer } from '@legendapp/state/react';
import { globalState } from '../../../../state/globalState';
import { databaseService } from '../../../../services/databaseService';
import { APP_CONSTANTS } from '../../../../config/constants';
import { getLogger } from '../../../../utils/logger';
import theme from '../../../../config/theme';
import PromptDialog from '../../../../components/PromptDialog';
import { ChecklistTemplateService } from '../../../../services/checklist/ChecklistTemplateService';
import { assertStructuralOnline } from '../../../../services/checklist/ChecklistStructuralOfflineGate';
import { checklistStructuralMutationService } from '../../../../services/checklist/ChecklistStructuralMutationService';
import { useWebAlert } from '../../../../components/WebAlert';
import {
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';

interface CopyOption { value: string; label: string };

const logger = getLogger('APP-(APP)-SETTINGS-CHECKLISTS-TEMPLATES');

// ==== LOG UTIL ==== //
//logger.info('TemplatesScreen module loaded');

interface TemplateItem {
  id: string;
  nome: string;
  versao: number;
  retired_at?: string | null;
}

const TemplatesScreen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const empresaId = globalState.company.data.get()?.id || globalState.user.profile.get()?.empresa_id || '';

  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogValue, setDialogValue] = useState('');
  const [copyFromId, setCopyFromId] = useState<string | null>(null);
  const [mode, setMode] = useState<'add' | 'rename'>('add');
  const { showAlert } = useWebAlert();
  const [copyOptions, setCopyOptions] = useState<CopyOption[]>([]);

  const showMessage = (title: string, msg: string, buttons?: any[]) => {
    // NOVO LOG PARA DEPURAÇÃO
    logger.info(`showMessage called | title="${title}" | buttons=${buttons?.length ?? 0}`);
    if (Platform.OS !== 'android') {
      showAlert(title, msg, buttons ?? [{ text: 'OK' }]);
    } else {
      Alert.alert(title, msg, buttons);
    }
  };

  const loadTemplates = async () => {
    if (!empresaId) return;
    logger.info(`loadTemplates iniciado | empresaId=${empresaId}`);
    setLoading(true);
    try {
      const result = await databaseService.listItems<TemplateItem>(
        APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
        { empresa_id: empresaId }
      );
      logger.info(`loadTemplates concluiu | total=${result.length}`);
      setTemplates((result as TemplateItem[]).filter(template => !template.retired_at));
    } catch (err) {
      logger.error('Erro ao carregar templates', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    logger.info('TemplatesScreen mounted, carregando templates');
    loadTemplates();
  }, []);

  const openAddDialog = () => {
    logger.info('Abrindo diálogo para adicionar modelo');
    setMode('add');
    setDialogValue('');
    setCopyFromId(null);
    const opts: CopyOption[] = [{ value: '', label: 'Em branco' }, ...templates.map(t => ({ value: t.id, label: t.nome }))];
    setCopyOptions(opts);
    setDialogVisible(true);
  };

  const openRenameDialog = (tpl: TemplateItem) => {
    logger.info(`Abrindo diálogo para renomear modelo ${tpl.id}`);
    setMode('rename');
    setDialogValue(tpl.nome);
    setCopyFromId(tpl.id);
    setDialogVisible(true);
  };

  const handleDelete = (tpl: TemplateItem) => {
    showMessage('Excluir Modelo', `Deseja excluir o modelo "${tpl.nome}"?\n\nO modelo deixará de aparecer para novos checklists. Os checklists anteriores e suas imagens serão preservados.`, [
      { text: 'Cancelar', style: 'cancel' },
      {
        text: 'Excluir', style: 'destructive', onPress: async () => {
          const gate = assertStructuralOnline('template', 'retire');
          if (!gate.allowed) {
            showMessage(gate.title, gate.message);
            return;
          }

          setLoading(true);
          try {
            const retiredAt = new Date().toISOString();
            await checklistStructuralMutationService.retireTemplate(tpl.id, retiredAt);
            logger.info(`Modelo ${tpl.id} aposentado sem remover historico local`);
            showMessage('Sucesso', 'Modelo aposentado com sucesso!');
            loadTemplates();
          } catch (err: any) {
            logger.error('Erro na exclusão completa do modelo:', err);
            showMessage('Erro', err?.message || 'Não foi possível excluir o modelo');
          } finally {
            setLoading(false);
          }
        }
      }
    ]);
  };

  const handleDialogConfirm = async (value: string, selectedType?: string) => {
    const name = value.trim();
    if (!name) return;
    try {
      logger.info(`handleDialogConfirm modo=${mode} valor="${name}" selectedType=${selectedType}`);
      if (mode === 'add') {
        const gate = assertStructuralOnline('template', 'create');
        if (!gate.allowed) {
          showMessage(gate.title, gate.message);
          return;
        }

        const selectedTemplateId = selectedType && selectedType !== '' ? selectedType : null;
        logger.info(`selectedTemplateId pós-dialog=${selectedTemplateId}`);
        if (selectedTemplateId) {
          logger.info(`Duplicando modelo ${selectedTemplateId} para novo nome "${name}"`);
          await new ChecklistTemplateService().duplicateTemplate(selectedTemplateId, empresaId, name);
        } else {
          // criar template vazio
          logger.info('Criando modelo vazio');
          await checklistStructuralMutationService.createTemplate({
            empresaId,
            nome: name,
            versao: 1,
          });
        }
        loadTemplates();
      } else if (mode === 'rename' && copyFromId) {
        const gate = assertStructuralOnline('template', 'edit');
        if (!gate.allowed) {
          showMessage(gate.title, gate.message);
          return;
        }

        logger.info(`Renomeando modelo ${copyFromId} para "${name}"`);
        await checklistStructuralMutationService.renameTemplate(copyFromId, name);
        loadTemplates();
      }
    } catch (err: any) {
      showMessage('Erro', err?.message || 'Operação não concluída');
    } finally {
      setDialogVisible(false);
    }
  };

  const goBack = () => router.back();

  const renderTemplate = (tpl: TemplateItem) => (
    <View key={tpl.id} style={styles.templateItem}>
      <View style={styles.templateInfo}>
        <MaterialCommunityIcons name="file-document-outline" size={24} color={theme.colors.primary.default} />
        <Text style={styles.templateName}>{tpl.nome}</Text>
      </View>
      <View style={styles.templateActions}>
        <TouchableOpacity onPress={() => openRenameDialog(tpl)}>
          <MaterialCommunityIcons name="pencil" size={20} color={theme.colors.text.secondary} />
        </TouchableOpacity>
        <TouchableOpacity accessibilityRole="button" accessibilityLabel={`Excluir modelo ${tpl.nome}`} onPress={() => handleDelete(tpl)} style={{ marginLeft: theme.spacing.md }}>
          <MaterialCommunityIcons name="trash-can" size={20} color={theme.colors.util.error} />
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    logger.info('Render TemplatesScreen'),
    <View style={styles.container}>
      {/* Header */}
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity style={styles.backButton} onPress={goBack}>
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Modelos de Checklist</Text>
        <View style={{ width: 40 }} />
      </View>

      {/* Content */}
      <ScrollView contentContainerStyle={[styles.scrollContent, { paddingBottom: scrollViewBottomPadding }]}>
        {loading ? (
          <ActivityIndicator size="large" color={theme.colors.primary.default} />
        ) : templates.length === 0 ? (
          <Text style={styles.noDataText}>Nenhum modelo encontrado</Text>
        ) : (
          templates.map(renderTemplate)
        )}
      </ScrollView>

      {/* Add Button */}
      <TouchableOpacity style={styles.fab} onPress={openAddDialog}>
        <MaterialCommunityIcons name="plus" size={24} color={theme.colors.text.inverse} />
      </TouchableOpacity>

      {/* Dialog */}
      <PromptDialog
        visible={dialogVisible}
        title={mode === 'add' ? 'Novo Modelo' : 'Renomear Modelo'}
        placeholder="Nome do modelo"
        initialValue={dialogValue}
        confirmLabel={mode === 'add' ? 'Adicionar' : 'Salvar'}
        onCancel={() => setDialogVisible(false)}
        onSubmit={handleDialogConfirm}
        types={mode === 'add' ? copyOptions : []}
        initialType={mode === 'add' ? (copyOptions[0]?.value as string ?? '') : undefined}
        message={mode === 'add' ? 'Escolha o modelo base' : undefined}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    width: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    flex: 1,
    fontSize: theme.fontSizes.xl,
    color: theme.colors.text.inverse,
    fontWeight: theme.fontWeights.bold as any,
    textAlign: 'center',
  },
  scrollContent: {
    padding: theme.spacing.md,
    paddingBottom: theme.spacing.xl,
  },
  templateItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.primary,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.md,
    ...theme.shadows.sm,
  },
  templateInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  templateName: {
    marginLeft: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    flexShrink: 1,
  },
  templateActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  fab: {
    position: 'absolute',
    right: theme.spacing.lg,
    bottom: theme.spacing.lg,
    backgroundColor: theme.colors.primary.default,
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    ...theme.shadows.lg,
  },
  noDataText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    textAlign: 'center',
    marginTop: theme.spacing.lg,
  },
});

export default TemplatesScreen;
